/* import __COLOCATED_TEMPLATE__ from './hubspot.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
/* eslint-disable @intercom/intercom/no-bare-strings */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type HandoffToExternalTicket from 'embercom/models/operator/visual-builder/step/handoff-to-external-ticket';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { get, post } from 'embercom/lib/ajax';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import { BlocksDocument } from '@intercom/composer-ember';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import { StandaloneComposerConfig } from 'embercom/objects/standalone/composer-config';
import type { Block } from 'embercom/models/common/blocks/block';

// HubSpot standard ticket states
const HUBSPOT_TICKET_STATES = [
  { value: 'new', text: 'New' },
  { value: 'waiting_on_contact', text: 'Waiting on contact' },
  { value: 'waiting_on_us', text: 'Waiting on us' },
  { value: 'closed', text: 'Closed' },
] as const;

type HubSpotTicketState = (typeof HUBSPOT_TICKET_STATES)[number]['value'];

interface HubSpotHandoffData {
  lookupContactByEmail: boolean;
  ticketTitle: BlocksDocument;
  ticketState: HubSpotTicketState;
}

export interface Args {
  step: HandoffToExternalTicket;
  readOnly?: boolean;
}

export default class HubspotExternalTicketHandoffStepComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: { app: { id: string; allowedAttributes: any[] } };
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare attributeService: $TSFixMe;

  @tracked connection: { connected: boolean } = { connected: false };
  @tracked messageHandler: ((event: MessageEvent) => void) | null = null;
  @tracked ticketTitleBlocksDoc: BlocksDocument;

  composerConfig!: StandaloneComposerConfig;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    // Initialize handoff data if it doesn't exist
    this.initializeHandoffData();

    this.ticketTitleBlocksDoc = new BlocksDocument(this.handoffData.ticketTitle?.blocks ?? []);

    // Initialize composer config for attribute inserter
    this.setupComposerConfig();

    taskFor(this.getConnection).perform();
  }

  willDestroy() {
    super.willDestroy();
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
    }
  }

  get isLoading() {
    return taskFor(this.getConnection).isRunning;
  }

  get hubspotTicketStates() {
    return HUBSPOT_TICKET_STATES;
  }

  get hasTicketTitleContent() {
    return (
      this.ticketTitleBlocksDoc &&
      this.ticketTitleBlocksDoc.blocks.length > 0 &&
      this.ticketTitleBlocksDoc.blocks.some(
        (block: Block) => block.text && block.text.trim().length > 0,
      )
    );
  }

  get allowedAttributes() {
    return this.attributeService.userEditorMetadataAndConversationAttributes.reject(
      (attr: any) => attr.isUserEvent,
    );
  }

  private initializeHandoffData() {
    let step = this.args.step as any;

    // Ensure handoffData object exists
    if (!step.handoffData) {
      step.handoffData = {};
    }

    // Initialize each property individually if not present
    if (step.handoffData.lookupContactByEmail === undefined) {
      step.handoffData.lookupContactByEmail = true;
    }

    if (!step.handoffData.ticketTitle) {
      step.handoffData.ticketTitle = new BlocksDocument([]);
    }

    if (!step.handoffData.ticketState) {
      step.handoffData.ticketState = 'new';
    }
  }

  private get handoffData(): HubSpotHandoffData {
    return this.args.step.handoffData as HubSpotHandoffData;
  }

  private setupComposerConfig() {
    let attributeResolver = new AttributeInfoResolver({
      attributes: this.appService.app.allowedAttributes,
      attributeAllowlist: this.attributeService.attributesToGroupList(this.allowedAttributes),
      conversationAttributes:
        this.attributeService.nonArchivedConversationCustomAttributesWithoutFile,
      includeCustomObjectAttributes: true,
      includeAppAttributes: false,
    });

    this.composerConfig = new StandaloneComposerConfig({
      resolver: attributeResolver,
      placeholder: this.intl.t(
        'standalone.external-ticket-handoff.hubspot.ticket-title-placeholder',
      ),
    });
  }

  @dropTask *getConnection() {
    this.connection = yield get('/standalone/hubspot/connection_status', {
      app_id: this.appService.app.id,
    });
  }

  @action
  handleConnect() {
    let width = 600;
    let height = 700;
    let left = (screen.width - width) / 2;
    let top = (screen.height - height) / 2;

    let popup = safeWindowOpen(
      `/standalone/hubspot/connect?app_id_code=${this.appService.app.id}`,
      'oauth-connect',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no`,
    );

    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
    }

    let handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type !== 'OAUTH_SUCCESS' && event.data.type !== 'OAUTH_ERROR') {
        return;
      }

      if (event.data.type === 'OAUTH_SUCCESS') {
        taskFor(this.getConnection).perform();
      } else if (event.data.type === 'OAUTH_ERROR') {
        console.error('OAuth connection failed:', event.data.error);
      }

      popup?.close();
      window.removeEventListener('message', handleMessage);
      this.messageHandler = null;
    };

    this.messageHandler = handleMessage;
    window.addEventListener('message', handleMessage);
  }

  @action
  async handleRevoke() {
    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('standalone.external-ticket-handoff.hubspot.revoke-connection-title'),
      body: this.intl.t('standalone.external-ticket-handoff.hubspot.revoke-connection-body'),
      confirmButtonText: this.intl.t('standalone.external-ticket-handoff.hubspot.revoke-button'),
      cancelButtonText: this.intl.t('standalone.external-ticket-handoff.hubspot.cancel-button'),
    });

    if (confirmed) {
      await post('/standalone/hubspot/revoke', {
        app_id: this.appService.app.id,
      });
      taskFor(this.getConnection).perform();
    }
  }

  @action
  handleLookupContactChange(checked: boolean) {
    this.handoffData.lookupContactByEmail = checked;
    this.args.step.handoffData = { ...this.handoffData };
  }

  @action
  handleTicketTitleChange(document: BlocksDocument) {
    this.ticketTitleBlocksDoc = document;
    this.handoffData.ticketTitle = document;
    this.args.step.handoffData = { ...this.handoffData };
  }

  @action
  handleTicketStateChange(state: HubSpotTicketState) {
    this.handoffData.ticketState = state;
    this.args.step.handoffData = { ...this.handoffData };
  }

  get stepTitle() {
    return this.intl.t('operator.workflows.visual-builder.create-external-ticket.title', {
      integration: 'HubSpot', // eslint-disable-line @intercom/intercom/no-bare-strings
      htmlSafe: true,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::ExternalTicketHandoffSteps::Hubspot': typeof HubspotExternalTicketHandoffStepComponent;
  }
}
